import {toast} from '@wandb/components';
import React, {Suspense, useContext, useRef} from 'react';

import {AccountSelectorContext} from '../components/Search/SearchNav/AccountSelectorContextProvider';
import {trackNavRightFeatureOpened} from '../services/analytics/productEvents';
import {useIsAdminModeActiveAndIsNotLocalEnv, viewingAs} from '../util/admin';
import {createPreloadableLazyComponent} from '../util/lazyImport';
import {useWBAgentController} from './chat/controller';
import {selectHasRunningThread} from './chat/store';
import {useStoreSelector} from './chat/store/context';
import {useUnseenSettledThread} from './chat/useUnseenSettledThread';
import {
  AgentChatProvider,
  AgentChatRuntimeBoundary,
} from './components/AgentChatProvider';
import {closeCustomizationSection} from './components/customizations/useCustomizationsStore';
import {useAgentChatAvailability} from './components/useAgentChatAvailability';
import {
  useTrackWBAgentComposer,
  useTrackWBAgentPanel,
} from './components/useTrackWBAgent';
import {useWBAgentIntroTooltip} from './components/useWBAgentIntroTooltip';
import {WBAgentChatButton} from './components/WBAgentChatButton';
import {WBAgentDrawerErrorBoundary} from './components/WBAgentErrorBoundary';
import {
  useWBAgentPresentationController,
  type WBAgentDisplayMode,
  WBAgentPageRouteSync,
  WBAgentPresentationControllerProvider,
} from './components/WBAgentPresentationController';
import {WBAgentSessionRestore} from './components/WBAgentSessionRestore';

const wbAgentSurface = createPreloadableLazyComponent(
  () => import('./components/WBAgentSurface'),
  module => module.WBAgentSurface
);
const LazyWBAgentSurface = wbAgentSurface.Component;
export const preloadWBAgentSurface = wbAgentSurface.preload;

function preloadWBAgentSurfaceOnIntent(): void {
  void preloadWBAgentSurface().catch(() => undefined);
}

/**
 * Component tree:
 * AriaEntrypoint → AriaEntrypointInner
 *   ├── WBAgentChatButton            "Ask ARIA" toggle
 *   └── WBAgentChatDrawer (docked, portaled to document.body; spans the
 *       │                  content area in page display mode)
 *       ├── WBAgentHeader
 *       ├── WBAgentChatHistorySidebar
 *       │   └── WBAgentCustomizations
 *       └── WBAgentPanelContent
 *           ├── WBAgentThread
 *           │   ├── WBAgentMessageAssistant
 *           │   ├── WBAgentMessageUser
 *           │   └── WBAgentComposer
 *           │       └── WBAgentEditor
 *           └── ProjectMemoryView
 */

export const AriaEntrypoint = ({
  displayMode = 'drawer',
  pageModeDisabled = false,
}: {
  /** The layout to show; `page` while the host's full-page route is current. */
  displayMode?: WBAgentDisplayMode;
  /** Only the standalone W&B header sets this: it has no full-page route. */
  pageModeDisabled?: boolean;
}) => {
  return (
    <AgentChatProvider>
      <WBAgentPresentationControllerProvider
        displayMode={displayMode}
        pageModeDisabled={pageModeDisabled}>
        <AriaEntrypointInner />
      </WBAgentPresentationControllerProvider>
    </AgentChatProvider>
  );
};

const AriaEntrypointInner = () => {
  const {isAvailable: isWorkspaceAgentEnabled} = useAgentChatAvailability();
  const hasRunningThread = useStoreSelector(selectHasRunningThread);
  const presentation = useWBAgentPresentationController();
  const {displayMode, isOpen} = presentation;
  const isPageMode = displayMode === 'page';
  const {selectedAccount} = useContext(AccountSelectorContext);
  const isAdminMode =
    useIsAdminModeActiveAndIsNotLocalEnv() || viewingAs() !== '';
  const {show: showIntroTooltip, hide: hideIntroTooltip} =
    useWBAgentIntroTooltip(isWorkspaceAgentEnabled);
  const {trackOpened, trackClosed, trackNavbarButtonViewed} =
    useTrackWBAgentPanel();
  const {trackEntryPointClicked} = useTrackWBAgentComposer();
  const {openChatToThread} = useWBAgentController();

  const activeToastRef = useRef<{threadId: string; key: string} | null>(null);
  // Handles notifying user via toast that a chat completed while the agent was minimized.
  // The callback only fires for live completions --> the toast won't re-appear
  // when the persisted value is rehydrated after a page reload.
  const unseenSettledThread = useUnseenSettledThread(
    isOpen,
    thread => {
      // Only ever show toast for the most recently completed thread
      const prev = activeToastRef.current;
      if (prev != null) {
        toast.dismiss(prev.key);
      }
      const key = toast.success('New response', {
        action: {
          label: 'Go to chat',
          onPress: () => {
            preloadWBAgentSurfaceOnIntent();
            toast.dismiss(key);
            openChatToThread(thread.threadId);
          },
        },
      });
      activeToastRef.current = {threadId: thread.threadId, key};
    },
    thread => {
      // onSeen func for dismissing toast
      const active = activeToastRef.current;
      if (active != null && active.threadId === thread.threadId) {
        toast.dismiss(active.key);
        activeToastRef.current = null;
      }
    }
  );

  return (
    <>
      {isWorkspaceAgentEnabled && (
        <WBAgentChatButton
          showIntroTooltip={showIntroTooltip}
          hideIntroTooltip={hideIntroTooltip}
          unseenSettledThread={unseenSettledThread}
          onImpression={trackNavbarButtonViewed}
          onPress={() => {
            // On the full-page route the button only shows that ARIA is open.
            if (isPageMode) {
              return;
            }
            preloadWBAgentSurfaceOnIntent();
            hideIntroTooltip();
            if (isOpen) {
              trackClosed();
              presentation.close();
              return;
            }
            trackNavRightFeatureOpened({
              feature: 'agent',
              selected_account: selectedAccount,
              is_admin_mode: isAdminMode,
            });
            trackEntryPointClicked('navbar');
            trackOpened();
            closeCustomizationSection();
            presentation.open();
          }}
          onHoverStart={preloadWBAgentSurfaceOnIntent}
          onFocus={preloadWBAgentSurfaceOnIntent}
          onPressStart={preloadWBAgentSurfaceOnIntent}
          isLoading={hasRunningThread}
          aria-expanded={isOpen}
        />
      )}
      <WBAgentDrawerErrorBoundary>
        <AgentChatRuntimeBoundary>
          <WBAgentSessionRestore
            onRestore={preloadWBAgentSurfaceOnIntent}
            skipRestore={isPageMode}
          />
          {isPageMode && <WBAgentPageRouteSync />}
          {isOpen && (
            <Suspense fallback={null}>
              <LazyWBAgentSurface />
            </Suspense>
          )}
        </AgentChatRuntimeBoundary>
      </WBAgentDrawerErrorBoundary>
    </>
  );
};
